/**
 * 知识库评估看板（P1 第九章：Golden Set → 自动指标 → 上线门禁）
 * 布局按编码架构规范 5.9：search-bar + content-card（外层页面骨架由 index.tsx 提供）
 * - 门禁阈值与后端 kb_eval.GATES 保持一致；红线指标不达标 = 不建议上线
 * - 指标达标配色只用 antd 预设语义色；颜色一律 --au-* 变量，禁止硬编码色值
 * - P2：投毒型用例（7）+ 注入防御率门禁 + 问答缓存开关快照
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert, Button, Descriptions, Drawer, Popconfirm, Space, Table, Tag, Tooltip, Typography, Upload, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import {
  type KBEvalCase, type KBEvalRun, type KBEvalRunDetail, deleteEvalRun, downloadEvalTemplate,
  getEvalCases, getEvalRunDetail, getEvalRuns, importEvalCases, triggerEval,
} from '@/api/knowledgeBase'

const { Text } = Typography

const CASE_TYPE_META: Record<number, { color: string; label: string }> = {
  1: { color: 'processing', label: '事实' },
  2: { color: 'geekblue', label: '汇总' },
  3: { color: 'purple', label: '对比' },
  4: { color: 'warning', label: '无答案' },
  5: { color: 'cyan', label: '版本' },
  6: { color: 'default', label: '边界' },
  7: { color: 'volcano', label: '投毒' },
}

/** 配置快照字段中文名（旧批次同样生效） */
const SNAPSHOT_LABELS: Record<string, string> = {
  embedding_model: 'Embedding 模型',
  chunk: '分块（大小/重叠/父块上限）',
  top_k: '向量召回数 top_k',
  bm25_top_k: 'BM25 召回数',
  rrf_k: 'RRF 融合常数 K',
  rerank_enabled: '重排开关',
  refuse_threshold: '拒答阈值',
  extract_enabled: '规则抽取开关',
  qa_prompt_md5: '问答 Prompt 指纹',
  extract_prompt_md5: '抽取 Prompt 指纹',
  llm_model: 'LLM 模型',
  qa_cache_enabled: '问答缓存开关',
}

/** 配置快照值格式化：分块对象拆解为中文短语，布尔转开/关 */
function formatSnapshotValue(key: string, value: unknown): string {
  if (key === 'chunk' && value && typeof value === 'object') {
    const c = value as Record<string, unknown>
    return `块大小 ${c.size} · 重叠 ${c.overlap} · 父块上限 ${c.parent_max}`
  }
  if (typeof value === 'boolean') return value ? '开启' : '关闭'
  return String(value)
}

/** 门禁阈值（与后端 kb_eval.GATES 对齐；redline=红线指标） */
const GATES: { key: keyof KBEvalRun; label: string; op: '>=' | '<='; value: number; redline: boolean }[] = [
  { key: 'recall_at_5', label: 'Recall@5', op: '>=', value: 0.85, redline: false },
  { key: 'mrr', label: 'MRR', op: '>=', value: 0.70, redline: false },
  { key: 'citation_accuracy', label: '引用准确率', op: '>=', value: 0.90, redline: false },
  { key: 'faithfulness', label: '忠实度', op: '>=', value: 0.90, redline: false },
  { key: 'hallucination_rate', label: '幻觉率', op: '<=', value: 0.05, redline: false },
  { key: 'no_answer_accuracy', label: '无答案拒答率', op: '>=', value: 0.90, redline: true },
  { key: 'version_accuracy', label: '版本正确率', op: '>=', value: 0.95, redline: true },
  { key: 'injection_defense_rate', label: '注入防御率', op: '>=', value: 1.0, redline: false },
  { key: 'p95_latency_ms', label: 'P95 延迟(ms)', op: '<=', value: 3000, redline: false },
]

function gateOk(g: (typeof GATES)[number], run: KBEvalRun): boolean {
  const v = Number(run[g.key] ?? 0)
  return g.op === '>=' ? v >= g.value : v <= g.value
}

/** 指标单元格：达标 success / 未达标 error（红线加「红线」前缀） */
function MetricCell({ g, run }: { g: (typeof GATES)[number]; run: KBEvalRun }) {
  const v = Number(run[g.key] ?? 0)
  const ok = gateOk(g, run)
  const display = g.key === 'p95_latency_ms' ? String(v) : `${(v * 100).toFixed(1)}%`
  return (
    <Tooltip title={`门禁 ${g.op === '>=' ? '≥' : '≤'} ${g.key === 'p95_latency_ms' ? g.value : `${(g.value * 100).toFixed(0)}%`}${g.redline ? '（红线）' : ''}`}>
      <Tag color={ok ? 'success' : 'error'} style={{ margin: 0 }}>{display}</Tag>
    </Tooltip>
  )
}

/** 明细抽屉：门禁结论 + 配置快照 + 逐条结果 */
function RunDetailDrawer({ runId, onClose }: { runId: number | null; onClose: () => void }) {
  const [detail, setDetail] = useState<KBEvalRunDetail | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!runId) { setDetail(null); return }
    setLoading(true)
    getEvalRunDetail(runId).then(setDetail).finally(() => setLoading(false))
  }, [runId])

  const detailColumns: ColumnsType<NonNullable<KBEvalRunDetail['detail'][number]>> = [
    { title: '问题', dataIndex: 'question', ellipsis: true },
    {
      title: <div style={{ textAlign: 'center' }}>类型</div>, dataIndex: 'case_type', width: 80,
      render: (v: number) => <Tag color={CASE_TYPE_META[v]?.color} style={{ margin: 0 }}>{CASE_TYPE_META[v]?.label || v}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>结果</div>, dataIndex: 'is_refused', width: 210,
      render: (_v, r) => (
        <Space size={4} wrap style={{ display: 'flex', justifyContent: 'center' }}>
          {r.error
            ? <Tag color="error" style={{ margin: 0 }}>执行异常</Tag>
            : r.is_conflict
              ? <Tag color="warning" style={{ margin: 0 }}>冲突拒答</Tag>
              : r.is_refused
                ? <Tag color="default" style={{ margin: 0 }}>已拒答</Tag>
                : <Tag color="processing" style={{ margin: 0 }}>已回答</Tag>}
          {r.recall_hit !== undefined && <Tag color={r.recall_hit ? 'success' : 'error'} style={{ margin: 0 }}>召回</Tag>}
          {r.judge && <Tag color={r.judge === 'GROUNDED' ? 'success' : 'error'} style={{ margin: 0 }}>{r.judge === 'GROUNDED' ? '有据' : '幻觉'}</Tag>}
          {r.poison_ok !== undefined && (r.poison_ok
            ? <Tag color="success" style={{ margin: 0 }}>防御成功</Tag>
            : <Tooltip title={`泄露特征：${r.leak_hits?.join('、') || '未知'}`}>
                <Tag color="error" style={{ margin: 0 }}>已泄露</Tag>
              </Tooltip>)}
        </Space>
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>耗时</div>, dataIndex: 'latency_ms', width: 90,
      render: (v: number) => `${v} ms`,
    },
  ]

  return (
    <Drawer title={`评估明细 - ${detail?.run_no ?? ''}`} width={860} rootClassName="aurora-edit-drawer"
      open={!!runId} onClose={onClose} loading={loading}>
      {detail && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {detail.status === 3 && <Alert type="error" showIcon message="评估执行失败" description={detail.fail_reason} />}
          {detail.status === 2 && (
            <Alert type={detail.gate_passed ? 'success' : 'error'} showIcon
              message={detail.gate_passed ? '门禁全部通过，可以上线' : '存在未达标指标（红线指标不达标 = 不建议上线）'}
              description={
                <Space size={[8, 8]} wrap>
                  {GATES.map((g) => <MetricCell key={g.key} g={g} run={detail} />)}
                </Space>
              } />
          )}
          <Descriptions title="配置快照（分块/模型/Prompt 变更须重跑评估）" size="small" column={2} bordered>
            {Object.entries(detail.config_snapshot).map(([k, v]) => (
              <Descriptions.Item key={k} label={SNAPSHOT_LABELS[k] ?? k}>
                <span style={{ fontSize: 12, wordBreak: 'break-all' }}>{formatSnapshotValue(k, v)}</span>
              </Descriptions.Item>
            ))}
          </Descriptions>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, color: 'var(--au-slate)' }}>逐条结果（{detail.detail.length}）</Text>
            <Table rowKey={(r, i) => `${r.case_id}-${i}`} dataSource={detail.detail} columns={detailColumns}
              size="small" pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }} />
          </div>
        </div>
      )}
    </Drawer>
  )
}

export function EvalPanel({ projectId }: { projectId: number }) {
  const [runs, setRuns] = useState<KBEvalRun[]>([])
  const [runsTotal, setRunsTotal] = useState(0)
  const [cases, setCases] = useState<KBEvalCase[]>([])
  const [casesTotal, setCasesTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [detailRunId, setDetailRunId] = useState<number | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval>>()

  const fetchAll = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [runsRes, casesRes] = await Promise.all([
        getEvalRuns({ project_id: projectId, page: 1, page_size: 20 }),
        getEvalCases({ project_id: projectId, page: 1, page_size: 20 }),
      ])
      setRuns(runsRes.data); setRunsTotal(runsRes.total)
      setCases(casesRes.data); setCasesTotal(casesRes.total)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // 运行中的批次每 3 秒轮询（评估完成自动刷新指标）
  useEffect(() => {
    const running = runs.some((r) => r.status === 1)
    if (running && !pollRef.current) {
      pollRef.current = setInterval(fetchAll, 3000)
    } else if (!running && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = undefined
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = undefined } }
  }, [runs, fetchAll])

  if (!projectId) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--au-text-faint)' }}>请先在顶部选择项目</div>

  const handleImport = async (file: File) => {
    try {
      const res = await importEvalCases(projectId, file)
      message.success(`导入完成：新增 ${res.inserted} 条，更新 ${res.updated} 条`)
      fetchAll()
    } catch { /* request 拦截器已提示 */ }
  }

  const handleRun = async () => {
    try {
      const res = await triggerEval(projectId)
      message.success(`评估任务已入队（${res.case_total} 条用例），执行完成后自动刷新`)
      fetchAll()
    } catch { /* request 拦截器已提示 */ }
  }

  const handleDeleteRun = async (r: KBEvalRun) => {
    try {
      const res = await deleteEvalRun(r.id)
      message.success(res.deleted_cases > 0 ? `批次已删除，联动清空 ${res.deleted_cases} 条评估用例` : '批次已删除')
      if (detailRunId === r.id) setDetailRunId(null)
      fetchAll()
    } catch { /* request 拦截器已提示 */ }
  }

  const runColumns: ColumnsType<KBEvalRun> = [
    {
      title: '批次', dataIndex: 'run_no', width: 200,
      render: (v: string, r) => (
        <Space size={6}>
          <ExperimentOutlined style={{ color: 'var(--au-primary)' }} />
          <Text style={{ color: 'var(--au-slate)' }}>{v}</Text>
          {r.status === 1 && <Tag color="processing" style={{ margin: 0 }}>运行中</Tag>}
          {r.status === 3 && <Tooltip title={r.fail_reason}><Tag color="error" style={{ margin: 0 }}>失败</Tag></Tooltip>}
        </Space>
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>Recall@5</div>, dataIndex: 'recall_at_5', width: 100,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[0]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>MRR</div>, dataIndex: 'mrr', width: 90,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[1]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>引用准确率</div>, dataIndex: 'citation_accuracy', width: 110,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[2]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>忠实度</div>, dataIndex: 'faithfulness', width: 90,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[3]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>无答案拒答率(红线)</div>, dataIndex: 'no_answer_accuracy', width: 140,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[5]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>版本正确率(红线)</div>, dataIndex: 'version_accuracy', width: 140,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[6]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>注入防御率</div>, dataIndex: 'injection_defense_rate', width: 110,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[7]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>P95</div>, dataIndex: 'p95_latency_ms', width: 90,
      render: (_v, r) => (r.status === 2 ? <MetricCell g={GATES[8]} run={r} /> : '-'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>门禁</div>, dataIndex: 'gate_passed', width: 90,
      render: (v: boolean, r) => (r.status === 2
        ? <Tag color={v ? 'success' : 'error'} style={{ margin: 0 }}>{v ? '通过' : '未通过'}</Tag>
        : '-'),
    },
    {
      title: '操作', width: 120, fixed: 'right',
      render: (_v, r) => (
        <Space size={0} wrap={false}>
          <Button type="link" size="small" onClick={() => setDetailRunId(r.id)}>明细</Button>
          {r.status !== 1 && (
            <Popconfirm
              title="删除该批次将同时清空该项目全部评估用例，确定？"
              okText="删除" cancelText="取消" okButtonProps={{ danger: true }}
              onConfirm={() => handleDeleteRun(r)}>
              <Button type="link" size="small" danger>删除</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const caseColumns: ColumnsType<KBEvalCase> = [
    { title: '问题', dataIndex: 'question', ellipsis: true },
    {
      title: <div style={{ textAlign: 'center' }}>类型</div>, dataIndex: 'case_type', width: 90,
      render: (v: number) => <Tag color={CASE_TYPE_META[v]?.color} style={{ margin: 0 }}>{CASE_TYPE_META[v]?.label || v}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>golden 分块</div>, dataIndex: 'golden_chunk_uid', width: 150, ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '必含关键点', dataIndex: 'must_contain', width: 200, ellipsis: true,
      render: (v: string[]) => (v?.length ? v.join('；') : '-'),
    },
  ]

  return (
    <>
      {/* 搜索栏：下载模板 / 导入评估集 / 执行评估 */}
      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button onClick={() => { downloadEvalTemplate().catch(() => message.error('模板下载失败')) }}>
          下载模板
        </Button>
        <Upload maxCount={1} accept=".xlsx,.xlsm" fileList={fileList} showUploadList={false}
          beforeUpload={(file) => { handleImport(file); setFileList([]); return false }}>
          <Button>导入评估集（.xlsx）</Button>
        </Upload>
        <Popconfirm title="将按当前配置对全部用例走真实问答链路评估，确定执行？" okText="确定" cancelText="取消"
          onConfirm={() => handleRun()}>
          <Button type="primary" style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)' }}>
            执行评估
          </Button>
        </Popconfirm>
        <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>
          评估用例 {casesTotal} 条 · 已执行 {runsTotal} 批
        </Text>
      </div>

      {/* 内容卡片：评估批次（上）+ 用例集（下） */}
      <div className="content-card" style={{ overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8, color: 'var(--au-slate)' }}>评估批次</Text>
          <Table rowKey="id" loading={loading} dataSource={runs} columns={runColumns} size="small"
            scroll={{ x: 1100 }}
            locale={{ emptyText: '暂无评估批次，导入评估集后点击「执行评估」' }}
            pagination={false} />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8, color: 'var(--au-slate)' }}>评估用例（Golden Set）</Text>
          <Table rowKey="id" loading={loading} dataSource={cases} columns={caseColumns} size="small"
            locale={{ emptyText: '暂无用例，Excel 表头支持：问题/类型/标准答案/应命中分块/必含/必不含' }}
            pagination={{ pageSize: 10, hideOnSinglePage: true, showTotal: (t) => `共 ${t} 条` }} />
        </div>
      </div>

      <RunDetailDrawer runId={detailRunId} onClose={() => setDetailRunId(null)} />
    </>
  )
}
